import * as React from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { JourneyClaimFailure } from "@volley/data";

import { fetchApi } from "../../util";
import { FetchError, logFetchError } from "../../util/fetchApi";
import { AuthState, useCurrentUser } from "../hooks/currentUser";

const FAILURE_MESSAGES: Record<JourneyClaimFailure, string> = {
    not_found: "We couldn't find that code. Check the link and try again.",
    not_journey: "This link isn't for an assessment.",
    expired:
        "This code has expired. Ask whoever ran your assessment for a new one.",
    used: "This code has already been used.",
    already_claimed: "This assessment is already on a Volley account.",
    wrong_player: "This assessment belongs to a different Volley account.",
    own_run:
        "You ran this assessment. Have the player scan the code to claim it.",
    unfinished:
        "This assessment isn't finished yet. Ask whoever ran it to finish it and show you a new code.",
};

function failureMessage(e: unknown): string {
    if (e instanceof FetchError) {
        const reason = (e.body as { reason?: JourneyClaimFailure } | null)
            ?.reason;
        if (reason && reason in FAILURE_MESSAGES) {
            return FAILURE_MESSAGES[reason];
        }
    }
    return "Something went wrong claiming your assessment. Try again in a moment.";
}

/**
 * Where a journey claim code's link and QR code land. Public, because the
 * player scanning it usually has no account yet: signed out, it offers to
 * create one (the code rides along and is redeemed at sign-up) or to log in
 * and come back here; signed in, it redeems straight away.
 */
export default function ClaimLanding(): React.JSX.Element {
    const { code = "" } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const { currentUser, authState } = useCurrentUser();
    const [error, setError] = React.useState<string>();
    const attempted = React.useRef(false);

    React.useEffect(() => {
        if (!currentUser || attempted.current) return;
        attempted.current = true;

        fetchApi<{ journeyInstanceUlid: string }>(
            `/api/invites/${encodeURIComponent(code)}/redeem`,
            "POST",
        )
            .then(({ journeyInstanceUlid }) => {
                // ShareSnackbar on the home screen announces it and links
                // to the result.
                navigate("/", {
                    replace: true,
                    state: { journeyInstanceShareUlid: journeyInstanceUlid },
                });
            })
            .catch((e: unknown) => {
                if (!(e instanceof FetchError) || e.statusCode >= 500) {
                    logFetchError(e);
                }
                setError(failureMessage(e));
            });
    }, [code, currentUser, navigate]);

    let body: React.ReactNode;
    if (authState === AuthState.LOADING || (currentUser && !error)) {
        body = (
            <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
            </Stack>
        );
    } else if (error) {
        body = (
            <Stack spacing={2}>
                <Typography>{error}</Typography>
                <Button variant="contained" component={RouterLink} to="/">
                    Go to Volley
                </Button>
            </Stack>
        );
    } else {
        body = (
            <Stack spacing={2}>
                <Typography>
                    Your assessment results are waiting. Create a free Volley
                    account, or log in, and we&apos;ll add them to your account.
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    component={RouterLink}
                    to={`/signup/email?invite=${encodeURIComponent(code)}`}
                >
                    Create account
                </Button>
                <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to="/login"
                    state={{ from: `/claim/${encodeURIComponent(code)}` }}
                >
                    I have an account
                </Button>
            </Stack>
        );
    }

    return (
        <Container maxWidth="xs" sx={{ py: 6 }}>
            <Stack spacing={3}>
                <Typography variant="h3" component="h1">
                    Claim your assessment
                </Typography>
                {body}
            </Stack>
        </Container>
    );
}
